import { useMemo, useState } from 'react'
import { availableRanges, pointsInRange, resolveRange, type ChartPoint, type ChartRangeId } from 'models/chart'

/** Every chart opens on the year to date; on data too short for that, its whole history. */
const DEFAULT_RANGES: ChartRangeId[] = ['YTD', 'All']

export interface ChartRange {
  ranges: ChartRangeId[]
  active: ChartRangeId
  /** The points inside the active range. */
  points: ChartPoint[]
  select: (range: ChartRangeId) => void
}

/** Range picker state for one series; `onSelect` runs on every pick, e.g. to clear a hover. */
export function useChartRange(
  points: ChartPoint[],
  defaults: ChartRangeId[] = DEFAULT_RANGES,
  onSelect?: () => void,
): ChartRange {
  const [picked, setPicked] = useState<ChartRangeId | null>(null)
  const ranges = useMemo(() => availableRanges(points), [points])
  const active = resolveRange(ranges, picked, defaults)
  const inRange = useMemo(() => pointsInRange(points, active), [points, active])
  const select = (range: ChartRangeId) => {
    setPicked(range)
    onSelect?.()
  }
  return { ranges, active, points: inRange, select }
}
